// Fahrzeug-Silhouetten als Inline-SVG, ein Eintrag je `iconKey` aus
// vehicleClasses.ts. Aus VehicleClassPicker herausgelöst, weil die Bilder
// inzwischen an zwei Stellen gebraucht werden: im Auswahl-Raster des
// KFZ-Formulars und als Typ-Symbol in der Kfz-Objektmappe (Risiken-Reiter).
//
// Die Pfade sind unverändert aus dem Picker übernommen. `currentColor` +
// `strokeWidth` bleiben, damit die Silhouette die Textfarbe des Kontexts
// erbt und auf hellem wie dunklem Grund funktioniert.

import * as React from "react";
import type { JSX } from "react";
import { VEHICLE_CLASSES, type VehicleClassDef } from "./vehicleClasses";

export type VehicleIconKey = VehicleClassDef["iconKey"];

const Svg = ({
  children,
  size,
}: {
  children: React.ReactNode;
  size: number;
}) => (
  <svg
    width={size}
    height={Math.round((size / 56) * 36)}
    viewBox="0 0 56 36"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const PATHS: Record<VehicleIconKey, JSX.Element> = {
  pkw: (
    <>
      <path d="M6 24l3-9a3 3 0 0 1 2.8-2h22.4a3 3 0 0 1 2.8 2l3 9" />
      <path d="M4 24h44v6a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-2H9v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
      <circle cx="14" cy="28" r="2.5" />
      <circle cx="38" cy="28" r="2.5" />
    </>
  ),
  lieferwagen: (
    <>
      <path d="M4 12h26v15H4z" />
      <path d="M30 16h11l5 7v4H30z" />
      <circle cx="12" cy="29" r="2.5" />
      <circle cx="38" cy="29" r="2.5" />
    </>
  ),
  lkw: (
    <>
      <path d="M4 8h22v18H4z" />
      <path d="M26 14h12l8 8v4H26z" />
      <circle cx="11" cy="29" r="2.5" />
      <circle cx="38" cy="29" r="2.5" />
      <circle cx="44" cy="29" r="2.5" />
    </>
  ),
  zugmaschine: (
    <>
      <path d="M4 16h12l4-8h6l3 8h3v9H4z" />
      <path d="M30 18h22v9H30z" />
      <circle cx="12" cy="29" r="2.5" />
      <circle cx="40" cy="29" r="2.5" />
      <circle cx="48" cy="29" r="2.5" />
    </>
  ),
  bus: (
    <>
      <path d="M5 8h44v18H5z" />
      <path d="M9 12h8v8H9zM21 12h8v8h-8zM33 12h8v8h-8z" />
      <circle cx="13" cy="29" r="2.5" />
      <circle cx="41" cy="29" r="2.5" />
    </>
  ),
  anhaenger: (
    <>
      <path d="M4 14h44v12H4z" />
      <path d="M48 20h6" />
      <circle cx="14" cy="29" r="2.5" />
      <circle cx="38" cy="29" r="2.5" />
    </>
  ),
  arbeitsmaschine: (
    <>
      <path d="M4 24h32v6H4z" />
      <path d="M14 24V12h12v12" />
      <path d="M28 24l8-10h6l4 10" />
      <circle cx="11" cy="30" r="3" />
      <circle cx="40" cy="30" r="3" />
    </>
  ),
  stapler: (
    <>
      <path d="M14 22V10h12v12" />
      <path d="M26 26h20" />
      <path d="M26 14v18" />
      <path d="M14 22H8v6" />
      <circle cx="14" cy="30" r="2.5" />
      <circle cx="22" cy="30" r="2.5" />
    </>
  ),
  krad: (
    <>
      <circle cx="12" cy="24" r="6" />
      <circle cx="44" cy="24" r="6" />
      <path d="M18 22l8-10h8l4 6h6" />
      <path d="M26 22l4-4h6" />
    </>
  ),
  sonderfahrzeug: (
    <>
      <path d="M6 24h44" />
      <path d="M10 24v-8h14l4-4h12v12" />
      <circle cx="16" cy="28" r="3" />
      <circle cx="40" cy="28" r="3" />
      <path d="M28 12v-4h6" />
    </>
  ),
};

/** Silhouette zu einem iconKey. */
export const VehicleSilhouette = ({
  iconKey,
  size = 56,
}: {
  iconKey: VehicleIconKey;
  size?: number;
}) => <Svg size={size}>{PATHS[iconKey]}</Svg>;

/**
 * Merkmalswert `vehicleClass` → iconKey. Der gespeicherte Wert ist der volle
 * Klassentext („Klasse 2 Lieferwagen Güterbeförderung bis 3.500kg"), deshalb
 * erst exakt vergleichen und nur ersatzweise auf Teiltreffer gehen. Unbekannt
 * → `sonderfahrzeug` statt gar nichts.
 */
export const vehicleIconKeyFor = (vehicleClass?: string | null): VehicleIconKey => {
  const raw = (vehicleClass ?? "").trim();
  if (!raw) return "sonderfahrzeug";
  const exact = VEHICLE_CLASSES.find((c) => c.value === raw);
  if (exact) return exact.iconKey;
  const lower = raw.toLowerCase();
  const partial = VEHICLE_CLASSES.find(
    (c) => lower.includes(c.short.toLowerCase()) || c.value.toLowerCase().includes(lower),
  );
  return partial?.iconKey ?? "sonderfahrzeug";
};
